import React, { useState, useEffect } from 'react'
import { supabase } from '@/integrations/supabase/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { useToast } from '@/hooks/use-toast'
import { Trash2, Edit, Plus, Save, X } from 'lucide-react'
import ImageUpload from '@/components/shared/ImageUpload'

interface ProcessStep {
  id: string
  step_number: number
  title: string
  description: string
  image_url?: string
  display_order: number
  is_active: boolean
}

const ProcessStepsManager = () => {
  const [processSteps, setProcessSteps] = useState<ProcessStep[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [isCreating, setIsCreating] = useState(false)
  const { toast } = useToast()

  const [formData, setFormData] = useState({
    step_number: 1,
    title: '',
    description: '',
    image_url: '',
    display_order: 0,
    is_active: true
  })

  useEffect(() => {
    fetchProcessSteps()
  }, [])

  const fetchProcessSteps = async () => {
    try {
      const { data, error } = await supabase
        .from('process_steps')
        .select('*')
        .order('display_order')

      if (error) throw error
      setProcessSteps(data || [])
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch process steps",
        variant: "destructive",
      })
    } finally {
      setIsLoading(false)
    }
  }

  const getNextStepNumber = () => {
    if (processSteps.length === 0) return 1
    const maxStepNumber = Math.max(...processSteps.map(step => step.step_number))
    return maxStepNumber + 1
  }

  const handleSave = async () => {
    try {
      if (editingId) {
        const { error } = await supabase
          .from('process_steps')
          .update(formData)
          .eq('id', editingId)
        
        if (error) throw error
        toast({
          title: "Success",
          description: "Process step updated successfully!",
        })
      } else {
        const { error } = await supabase
          .from('process_steps')
          .insert([formData])
        
        if (error) throw error
        toast({
          title: "Success",
          description: "Process step created successfully!",
        })
      }
      
      fetchProcessSteps()
      resetForm()
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save process step",
        variant: "destructive",
      })
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this process step?')) return

    try {
      const { error } = await supabase
        .from('process_steps')
        .delete()
        .eq('id', id)

      if (error) throw error
      
      toast({
        title: "Success",
        description: "Process step deleted successfully!",
      })
      fetchProcessSteps()
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete process step",
        variant: "destructive",
      })
    }
  }

  const handleEdit = (step: ProcessStep) => {
    setFormData({
      step_number: step.step_number,
      title: step.title,
      description: step.description,
      image_url: step.image_url || '',
      display_order: step.display_order,
      is_active: step.is_active
    })
    setEditingId(step.id)
    setIsCreating(false)
  }

  const resetForm = () => {
    setFormData({
      step_number: 1,
      title: '',
      description: '',
      image_url: '',
      display_order: 0,
      is_active: true
    })
    setEditingId(null)
    setIsCreating(false)
  }

  if (isLoading) {
    return <div>Loading process steps...</div>
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Process Steps Manager</h2>
        <Button onClick={() => {
          setIsCreating(true)
          setFormData(prev => ({ ...prev, step_number: getNextStepNumber() }))
        }} disabled={isCreating || !!editingId}>
          <Plus className="w-4 h-4 mr-2" />
          Add Process Step
        </Button>
      </div>

      {(isCreating || editingId) && (
        <Card>
          <CardHeader>
            <CardTitle>{editingId ? 'Edit Process Step' : 'Create New Process Step'}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="step_number">Step Number</Label>
                <Input
                  id="step_number"
                  type="number"
                  value={formData.step_number}
                  onChange={(e) => setFormData({ ...formData, step_number: parseInt(e.target.value) })}
                />
              </div>
              <div>
                <Label htmlFor="display_order">Display Order</Label>
                <Input
                  id="display_order"
                  type="number"
                  value={formData.display_order}
                  onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) })}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., Definition & Understanding"
              />
            </div>

            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Detailed description of this process step..."
                rows={4}
              />
            </div>

            <div>
              <ImageUpload
                currentImageUrl={formData.image_url}
                onImageUpload={(url) => setFormData({ ...formData, image_url: url })}
                onImageRemove={() => setFormData({ ...formData, image_url: '' })}
                label="Process Step Image"
                folder="process-steps"
              />
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              />
              <Label htmlFor="is_active">Active</Label>
            </div>

            <div className="flex gap-2">
              <Button onClick={handleSave}>
                <Save className="w-4 h-4 mr-2" />
                Save
              </Button>
              <Button variant="outline" onClick={resetForm}>
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-4">
        {processSteps.map((step) => (
          <Card key={step.id}>
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    Step {step.step_number}: {step.title}
                    {!step.is_active && <span className="text-sm text-muted-foreground">(Inactive)</span>}
                  </CardTitle>
                  <CardDescription>{step.description}</CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEdit(step)}
                    disabled={isCreating || editingId === step.id}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(step.id)}
                    disabled={isCreating || editingId !== null}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            {step.image_url && (
              <CardContent>
                <img
                  src={step.image_url}
                  alt={step.title}
                  className="w-full h-32 object-cover rounded-md"
                />
              </CardContent>
            )}
          </Card>
        ))}
      </div>
    </div>
  )
}

export default ProcessStepsManager